import { useState, useMemo, useEffect, useCallback } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useWireState, useWireValue } from '@forminator/react-wire'
import * as store from '@store'
import * as actions from '@actions'
import useEffectOnce from '@hook/useEffectOnce'
import { jsonClone, deepmergeWithArrays } from '@utils'

export const RECORD_LOADING_TYPE = {
    IDLE: 'idle',
    LOADING: 'loading',
    LOADED: 'loaded',
}

const DocumentViewerModalViewModel = () => {
    
    //region State
    
    const [, setSearchParams] = useSearchParams()
    
    const [recordLoading, setRecordLoading] = useState(RECORD_LOADING_TYPE.IDLE)
    const [pageIndex, setPageIndex] = useState(0)
    const [annotationCompleted, setAnnotationCompleted] = useState(
        store.documentViewerModalDocument.getValue()?.annotationCompleted)
    
    /**
     * Map of { pageId => [matches, ...] }
     */
    const [matches, setMatches] = useState({})
    
    const [record, setRecord] = useWireState(store.documentViewerModalDocument)
    const pdfLoading = useWireValue(store.pdfRendererLoading)
    const pdfPageCount = useWireValue(store.pdfRendererPageCount)
    
    //endregion State
    
    //region Memos
    
    const open = useMemo(() => record?.id?.length > 0, [record])
    
    /**
     * Matches for the current page
     * 
     * @type {{all, next, current, counts: {all: *, next: *, current: *, prev: *}, prev}}
     */
    const pageMatches = useMemo(() => {
        
        const result = {
            all: matches ?? [],
            prev: matches?.[pageIndex - 1] ?? [],
            next: matches?.[pageIndex + 1] ?? [],
            current: matches?.[pageIndex] ?? [],
        }
        
        return {
            ...result,
            counts: {
                all: Object.values(result.all).reduce((acc, it) => acc + it.length, 0),
                prev: result.prev.length,
                next: result.next.length,
                current: result.current.length,
            },
        }
        
    }, [pageIndex, matches])
    
    /**
     * Finds the previous page index that has search matches
     *
     * @type {Number|null}
     */
    const prevPageWithMatchesIndex = useMemo(() => {
        
        let prevPageIndex = pageIndex
        
        while (prevPageIndex > 0) {
            
            prevPageIndex--
            
            if (pageMatches?.all?.[prevPageIndex]?.length)
                return prevPageIndex
            
        }
        
        return null
        
    }, [pageIndex, pageMatches])
    
    /**
     * Finds the next page index that has search matches
     *
     * @type {Number|null}
     */
    const nextPageWithMatchesIndex = useMemo(() => {
        
        const pageCount = Object.keys(pageMatches.all ?? {}).length
        
        if (!pageCount) return null
        
        let nextPageIndex = pageIndex
        
        while (nextPageIndex <= pageCount) {
            
            nextPageIndex++
            
            if (pageMatches?.all?.[nextPageIndex]?.length)
                return nextPageIndex
            
        }
        
        return null
        
    }, [pageIndex, pageMatches])
    
    //endregion Memos
    
    //region Methods
    
    // Simplified version of annotation results
    const updateMatches = useCallback(value => {
        
        if (!value) return
        // console.log('@bbox updateMatches:', JSON.stringify(value, null, 2))
        setMatches(Object.keys(value).reduce((acc, pageIndex) => ({
            ...acc,
            [pageIndex]: value[pageIndex].reduce((acc, result) => {
                
                // Edge case: skip results without IDs or we end up with weird UI pagination
                if (!result?.id) return acc
                
                return acc.concat({
                    id: result.id,
                    value: {
                        labels: result?.labels ?? [],
                        text: result?.text ?? [],
                    },
                })
                
            }, []),
        }), {}))
        
    }, [])
    
    const handleClose = () => {
        
        setPageIndex(0)
        
        setSearchParams(prev => {
            prev.delete('documentId')
            return prev
        })
    }
    
    //endregion Methods
    
    //region Hooks
    
    useEffectOnce(() => {
        
        // If we don't have any matches on the current page,
        // we do have a page with matches, and it's not the current page,
        // skip directly to that page (index)
        if (pageMatches !== null && !Object.keys(pageMatches.current).length &&
            nextPageWithMatchesIndex !== null &&
            nextPageWithMatchesIndex !== pageIndex)
            setPageIndex(nextPageWithMatchesIndex)
        
        return true
        
    }, [pageMatches.current, nextPageWithMatchesIndex, pageIndex])
    
    useEffect(() => {
        if (!record?.id) return
        // i can only fetch if record loading is idle
        if (recordLoading !== RECORD_LOADING_TYPE.IDLE) return
        
        setRecordLoading(RECORD_LOADING_TYPE.LOADING)
        
        // Make sure to deepmerge the ES result with the full DB result here,
        // since the ES result might have extra props, like `rawOcrWords`
        console.log('Fetching full document', record)
        actions.fetchDocumentById(record.id)
            .then(fullRecord => {
                
                // record = ES record, fullRecord = full document
                const mergedRecord = deepmergeWithArrays(jsonClone(record), jsonClone(fullRecord))
                
                mergedRecord.pages?.forEach((page, i) => {
                    
                    const foundAnnotations = fullRecord.pages
                        ?.find(it => it.pageIndex === page.pageIndex)
                        ?.annotations?.[0]?.annotations
                    
                    if (foundAnnotations)
                        mergedRecord.pages[i].annotations = foundAnnotations
                    
                })
                
                setRecord(mergedRecord)
                setAnnotationCompleted(fullRecord.annotationCompleted)
                
            })
            .catch(e => console.error('Failed to fetch full document', e))
            .finally(() => setRecordLoading(RECORD_LOADING_TYPE.LOADED))
        
    }, [record, recordLoading])
    
    useEffect(() => {
        
        // Reset this whenever the record changes to null
        if (!record)
            setRecordLoading(RECORD_LOADING_TYPE.IDLE)
        
    }, [record])
    
    const toggleAnnotationCompleted = async documentId => {
        setAnnotationCompleted(prev => !prev)
        const doc = await actions.toggleAnnotationCompleted(documentId)
        
        if (doc)
            setAnnotationCompleted(doc.annotationCompleted)
    }
    
    //endregion Hooks
    
    return {
        
        // State
        recordLoading,
        pdfLoading,
        pdfPageCount,
        pageIndex,
        setPageIndex,
        matches,
        setMatches,
        record,
        setRecord,
        
        // Memos
        open,
        pageMatches,
        prevPageWithMatchesIndex,
        nextPageWithMatchesIndex,
        
        // Methods
        updateMatches,
        handleClose,
        
        annotationCompleted,
        toggleAnnotationCompleted,
        
    }
    
}

export default DocumentViewerModalViewModel
